/* Edit lookup table */
import '@progress/kendo-ui';

import { groupBy, orderBy, process } from '@progress/kendo-data-query';
import { Grid, GridColumn, GridToolbar } from '@progress/kendo-react-grid';
import _ from 'lodash';
import * as React from 'react';
import { Button, ButtonGroup, ButtonToolbar, Modal, Row, Well } from 'react-bootstrap';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { push } from 'react-router-redux';
import { compose } from 'redux';
import { connectRequest } from 'redux-query';

import * as MutateActions from '../actions/mutate';
import * as AppComp from '../common/app-component-base';
import { BootstrapColXl as Col } from '../common/bootstrap/bootstrapcolxl';
import { Formatters } from '../common/formatters';
import * as KendoWrappers from '../common/kendoforms/componentwrappers';
import { NavigationLinks } from '../common/navigation';
import { WhiteBoxPageSection } from '../common/whiteboxpagesection';
import ApiEndpoints from '../config/apiendpoints';
import * as LK from '../config/apilookupkeys';
import * as DetailFormCommon from '../inventory/detail-form/common';
import * as ReduxQueries from '../queries';
import CellDropDown from './cell-dropdown';
import CellHtml from './cell-html';
import EditGridCommandCell from './edit-grid-command-cell';

export interface IEditLookupTableProps extends AppComp.IAppComponentProps {
    deleteLookupObj?: any;
    forceRequest?: any;
    lookups?: any;
    navigateTo?: any;
    newInventoryComponent?: any;
    newLookupObj?: any;
    tableName?: any;
    updateLookupObj?: any;
}

export interface IEditLookupTableState extends AppComp.IAppComponentState {
    additionalGridData?: any;
    addNewData?: any;
    editData?: any;
    formChanges?: any;
    gridSort?: any;
    showModalDeleteLookupObj: boolean;
}

class EditLookupTable extends AppComp.AppComponent<IEditLookupTableProps, IEditLookupTableState> {
    public readonly GridEditField: string = 'editField';
    public readonly GridNewRecordField: string = 'newRecordField';
    public readonly GridHideRecordField: string = 'hideRecordField';

    public constructor(props: IEditLookupTableProps) {
        super(props, 'app.lookup-tables.');

        this.state = {
            editData: {},
            addNewData: false,
            additionalGridData: undefined,
            formChanges: {},
            gridSort: undefined,
            showModalDeleteLookupObj: false,
        };
    }

    public BuildGridHtmlEditorCell = (props) => (
        <CellHtml
            recordEditingField={this.GridEditField}
            handleInputChange={(a) => { this.handleResumeHtmlInputChange(a); }}
            {...props}
        />
    )

    // BUTTON HANDLERS
    public handleEnterEdit(d: any) {

        this.setState({
            editData: _.cloneDeep(d),
            additionalGridData: undefined
        });
    }

    public handleEnterDetails(d: any, tn: any = '') {
        const { tableName, ...rest } = this.props;

        let childObjId = Formatters.getStr(d, '_id');

        const t = (_.isEmpty(tn)) ? tableName : tn;

        this.props.navigateTo(NavigationLinks.lookupTableEditChildObj(t, childObjId));
    }

    public handleAddNewButtonDisabled(e: any) {
        let result = false;

        switch (e) {
            case LK.LookupKeys.ComponentLocations:
                result = true;
                break;

            default:
                result = false;
                break;
        }

        return result;
    }

    public handleAddNewButton(lookupTableName: string, e: any) {
        switch (lookupTableName) {
            case LK.LookupKeys.Components: {
                const token = _.get(this.props, 'authsession.access_token', '');

                const changeData = _.merge(
                    {},
                    {
                        obj: {
                            _id: 'new'
                        }
                    },
                );

                this.props.newInventoryComponent(changeData, token, false).then(response => {
                    // We can promise-chain on the mutation action to trigger state
                    // transitions when it succeeds or fails.

                    if (response.status >= 200 && response.status < 300) {
                        // this.originalFormDataCache = {};

                        // FORCE RELOAD
                        const newInvCompObjId = Formatters.getStr(response, 'body._id');

                        this.props.navigateTo(NavigationLinks.componentEdit(newInvCompObjId));
                    } else {
                        // Error during creation...
                    }
                });
                break;
            }

            default: {
                let addNewLookupObj = {
                    name: '',
                    description: '',
                    _id: '_new_associationType',
                    associationTypeId: undefined,
                    [this.GridEditField]: true,
                    [this.GridNewRecordField]: true,
                    [this.GridHideRecordField]: false,
                };

                this.setState({
                    addNewData: true,
                    editData: addNewLookupObj,
                    additionalGridData: addNewLookupObj
                });
                break;
            }
        }
    }

    public handleConfirmRemove() {
        const { tableName } = this.props;
        const token = _.get(this.props, 'authsession.access_token', '');

        const curEdit = _.get(this.state, 'editData', {});
        const obj = _.omit(curEdit, 'deleteAssocTypeDestId');
        const destObj = { _id: Formatters.getStr(curEdit, 'deleteAssocTypeDestId') };
        const changeData = _.merge(
            {},
            { obj: obj },
            { tableName: tableName },
            { destObj: destObj },
        );

        this.props.deleteLookupObj(changeData, token, false).then(response => {
            // We can promise-chain on the mutation action to trigger state
            // transitions when it succeeds or fails.

            if (response.status >= 200 && response.status < 300) {
                this.setState({
                    editData: {},
                    addNewData: false,
                    additionalGridData: undefined,
                    showModalDeleteLookupObj: false,
                });
                this.props.forceRequest();
            } else {
                // Error during creation...
            }
        });
    }

    public handleSave(d: any) {
        const { tableName } = this.props;

        const token = _.get(this.props, 'authsession.access_token', '');

        const changeData = _.merge(
            {},
            { obj: d },
            { tableName: tableName },
        );

        this.props.updateLookupObj(changeData, token, false).then(response => {
            // We can promise-chain on the mutation action to trigger state
            // transitions when it succeeds or fails.

            if (response.status >= 200 && response.status < 300) {
                this.setState({
                    editData: {},
                    addNewData: false,
                    additionalGridData: undefined,
                });
                this.props.forceRequest();
            } else {
                // Error during creation...
            }
        });
    }

    public handleCreate(d: any) {
        const { tableName } = this.props;

        const token = _.get(this.props, 'authsession.access_token', '');

        const changeData = _.merge(
            {},
            { obj: d },
            { tableName: tableName },
        );

        this.props.newLookupObj(changeData, token, false).then(response => {
            // We can promise-chain on the mutation action to trigger state
            // transitions when it succeeds or fails.

            if (response.status >= 200 && response.status < 300) {
                this.setState({
                    editData: {},
                    addNewData: false,
                    additionalGridData: undefined,
                });
                this.props.forceRequest();
            } else {
                // Error during creation...
            }
        });
    }

    public handleCancel(d: any) {
        this.setState({
            editData: {},
            addNewData: false,
            additionalGridData: undefined,
        });
    }

    public handleGridItemChange(e: any) {
        let prev = _.cloneDeep(_.get(this.state, 'editData', {}));

        let n = Formatters.getStr(e, 'field');
        let v = _.get(e, 'value');

        if (!n) { return; }

        _.set(prev, n, v);

        this.setState({ editData: prev });
    }

    public handleCellDropdownChange(e: any) {
        // e.target.value = { name: 'best cost', value: '1' }
        let prev = _.cloneDeep(_.get(this.state, 'editData', {}));

        let val = _.get(e, 'target.value', {});

        _.set(prev, 'replacementCostType.replacementCostTypeId', val.value);
        _.set(prev, 'replacementCostType.name', val.text);

        // console.log('handleCellDropdownChange', { e, prev, val });

        this.setState({ editData: prev });
    }

    public handleResumeHtmlInputChange(v: any, field: string = 'resume') {
        let prev = _.cloneDeep(_.get(this.state, 'editData', {}));

        prev.resume = v;

        this.setState({ editData: prev });
    }

    public handleBackButton(e: any) {
        this.props.navigateTo(NavigationLinks.lookupTables());
    }

    public handleShowModalDeleteLookupObj(e: any, selectedDataItem: any) {
        this.setState({ showModalDeleteLookupObj: true, editData: _.cloneDeep(selectedDataItem) });
    }

    public handleCloseModalDeleteLookupObj() {
        this.setState({ showModalDeleteLookupObj: false, editData: {} });
    }

    // HELPERS
    public getGridCol(fieldName: string, colTitle: string, colOptions: any = {}) {
        const result = _.merge(
            {},
            {
                f: fieldName,
                t: colTitle,
                p: colOptions,
            });

        return result;
    }

    // RENDERS
    public renderHeaderButtonToolbar() {
        return (
            <div>
                <Well className="header-well-buttontoolbar">
                    <Row>
                        <Col xs={3}>
                            <ButtonToolbar>
                                <ButtonGroup>
                                    <Button bsSize="sm" bsStyle="info" onClick={e => { this.handleBackButton(e); }}><i className="fa fa-database" /> {this.appTextCommon('buttons.view-lookup-tables')}</Button>
                                </ButtonGroup>
                            </ButtonToolbar>
                        </Col>
                    </Row>
                </Well>
            </div>
        );
    }

    public renderGridCol(c: any, i?: any) {
        let props: any = {
            key: `col-${i}`,
        };

        if (Formatters.getStr(c, 'f')) { _.merge(props, { field: Formatters.getStr(c, 'f') }); }
        if (Formatters.getStr(c, 't')) { _.merge(props, { title: Formatters.getStr(c, 't') }); }
        if (_.get(c, 'p', {})) { _.merge(props, _.get(c, 'p', {})); }

        let result = (<GridColumn {...props} />);

        return result;
    }

    public renderModalDeleteLookupObj = (formData: any) => {
        const { tableName, ...rest } = this.props;

        const defaultInputProps = { colLabelXs: 4, colInputXs: 7 };

        const inputProps = _.merge({}, defaultInputProps, {
            onChange: (n, v) => { console.log({ n, v }); },
        });

        const inputPropsFullWidth = _.merge({}, defaultInputProps, {
            onChange: (n, v) => { console.log({ n, v }); },
            colInputXs: 12,
            colLabelXs: 12,
        });

        const inputPropsVirtualizedSelect = _.merge({}, defaultInputProps, {
            className: 'inventory-detail-input-full-width',
        });

        const curEditData = _.get(this.state, 'editData', {});
        let lookupObjName = '';
        let modalTitle = '';
        let modalNote = '';
        let modalSuccessButtonText = '';
        let disabled = (Formatters.getStr(curEditData, 'deleteAssocTypeDestId') === '') ? true : false;

        let modalBody = (<div />);
        switch (tableName) {
            case LK.LookupKeys.AssociationTypes: {
                lookupObjName = `${Formatters.getStr(curEditData, 'name')} (${Formatters.getStr(curEditData, 'associationTypeId')})`;
                modalTitle = this.appText('modals.delete-lookup-obj.association-types.title');
                modalNote = this.appText('modals.delete-lookup-obj.association-types.note');
                modalSuccessButtonText = this.appText('modals.delete-lookup-obj.association-types.delete-object');
                modalBody = (
                    <div>
                        <div className="lookup-tables-modal-delete-lookup-obj-warning-containter">
                            {modalNote}
                        </div>

                        <div>
                            {this.appText('modals.delete-lookup-obj.association-types.note-2')}
                        </div>

                        <div>
                            <DetailFormCommon.InventoryDetailFormSection title="">
                                <KendoWrappers.KendoDisabledInputHidden
                                    name="deleteLookupObj.targetAssociationType"
                                    label={this.appText('modals.delete-lookup-obj.association-types.pending-deletion')}
                                    value={lookupObjName}
                                    {...inputProps}
                                />
                                <KendoWrappers.KendoDropdownListInput
                                    name="deleteLookupObj.destObjId"
                                    clearable={false}
                                    label={this.appText('modals.delete-lookup-obj.association-types.choose-default-type')}
                                    value={_.get(this.state, 'editData.deleteAssocTypeDestId')}
                                    data={_.get(formData, 'options.associationTypes')}
                                    textField="text"
                                    valueField="value"
                                    onChange={(o) => {
                                        const newVal = _.get(o, 'target.value');
                                        let ed = _.get(this.state, 'editData');
                                        ed.deleteAssocTypeDestId = newVal;
                                        this.setState({ editData: ed });
                                    }}
                                    {...inputPropsVirtualizedSelect}
                                />
                            </DetailFormCommon.InventoryDetailFormSection>
                        </div>
                    </div>
                );
                break;
            }

            case LK.LookupKeys.ClientTypes: {
                lookupObjName = `${Formatters.getStr(curEditData, 'name')} (${Formatters.getStr(curEditData, 'clientTypeId')})`;
                modalTitle = this.appText('modals.delete-lookup-obj.client-types.title');
                modalNote = this.appText('modals.delete-lookup-obj.client-types.note');
                modalSuccessButtonText = this.appText('modals.delete-lookup-obj.client-types.delete-object');
                modalBody = (
                    <div>
                        <div className="lookup-tables-modal-delete-lookup-obj-warning-containter">
                            {modalNote}
                        </div>

                        <div>
                            {this.appText('modals.delete-lookup-obj.client-types.note-2')}
                        </div>

                        <div>
                            <DetailFormCommon.InventoryDetailFormSection title="">
                                <KendoWrappers.KendoDisabledInputHidden
                                    name="deleteLookupObj.targetClientType"
                                    label={this.appText('modals.delete-lookup-obj.client-types.pending-deletion')}
                                    value={lookupObjName}
                                    {...inputProps}
                                />
                                <KendoWrappers.KendoDropdownListInput
                                    name="deleteLookupObj.destObjId"
                                    clearable={false}
                                    label={this.appText('modals.delete-lookup-obj.client-types.choose-default-type')}
                                    value={_.get(this.state, 'editData.deleteAssocTypeDestId')}
                                    data={_.get(formData, 'options.clientTypes')}
                                    textField="text"
                                    valueField="value"
                                    onChange={(o) => {
                                        const newVal = _.get(o, 'target.value');
                                        let ed = _.get(this.state, 'editData');
                                        ed.deleteAssocTypeDestId = newVal;
                                        this.setState({ editData: ed });
                                    }}
                                    {...inputPropsVirtualizedSelect}
                                />
                            </DetailFormCommon.InventoryDetailFormSection>
                        </div>
                    </div>
                );
                break;
            }

            case LK.LookupKeys.RoofTypes: {
                lookupObjName = `${Formatters.getStr(curEditData, 'name')} (${Formatters.getStr(curEditData, 'roofTypeId')})`;
                modalTitle = this.appText('modals.delete-lookup-obj.roof-types.title');
                modalNote = this.appText('modals.delete-lookup-obj.roof-types.note');
                modalSuccessButtonText = this.appText('modals.delete-lookup-obj.roof-types.delete-object');
                modalBody = (
                    <div>
                        <div className="lookup-tables-modal-delete-lookup-obj-warning-containter">
                            {modalNote}
                        </div>

                        <div>
                            {this.appText('modals.delete-lookup-obj.roof-types.note-2')}
                        </div>

                        <div>
                            <DetailFormCommon.InventoryDetailFormSection title="">
                                <KendoWrappers.KendoDisabledInputHidden
                                    name="deleteLookupObj.targetRoofType"
                                    label={this.appText('modals.delete-lookup-obj.roof-types.pending-deletion')}
                                    value={lookupObjName}
                                    {...inputProps}
                                />
                                <KendoWrappers.KendoDropdownListInput
                                    name="deleteLookupObj.destObjId"
                                    clearable={false}
                                    label={this.appText('modals.delete-lookup-obj.roof-types.choose-default-type')}
                                    value={_.get(this.state, 'editData.deleteAssocTypeDestId')}
                                    data={_.get(formData, 'options.roofTypes')}
                                    textField="text"
                                    valueField="value"
                                    onChange={(o) => {
                                        const newVal = _.get(o, 'target.value');
                                        let ed = _.get(this.state, 'editData');
                                        ed.deleteAssocTypeDestId = newVal;
                                        this.setState({ editData: ed });
                                    }}
                                    {...inputPropsVirtualizedSelect}
                                />
                            </DetailFormCommon.InventoryDetailFormSection>
                        </div>
                    </div>
                );
                break;
            }

            case LK.LookupKeys.FencingTypes: {
                lookupObjName = `${Formatters.getStr(curEditData, 'name')} (${Formatters.getStr(curEditData, 'fencingTypeId')})`;
                modalTitle = this.appText('modals.delete-lookup-obj.fencing-types.title');
                modalNote = this.appText('modals.delete-lookup-obj.fencing-types.note');
                modalSuccessButtonText = this.appText('modals.delete-lookup-obj.fencing-types.delete-object');
                modalBody = (
                    <div>
                        <div className="lookup-tables-modal-delete-lookup-obj-warning-containter">
                            {modalNote}
                        </div>

                        <div>
                            {this.appText('modals.delete-lookup-obj.fencing-types.note-2')}
                        </div>

                        <div>
                            <DetailFormCommon.InventoryDetailFormSection title="">
                                <KendoWrappers.KendoDisabledInputHidden
                                    name="deleteLookupObj.targetFencingType"
                                    label={this.appText('modals.delete-lookup-obj.fencing-types.pending-deletion')}
                                    value={lookupObjName}
                                    {...inputProps}
                                />
                                <KendoWrappers.KendoDropdownListInput
                                    name="deleteLookupObj.destObjId"
                                    clearable={false}
                                    label={this.appText('modals.delete-lookup-obj.fencing-types.choose-default-type')}
                                    value={_.get(this.state, 'editData.deleteAssocTypeDestId')}
                                    data={_.get(formData, 'options.fencingTypes')}
                                    textField="text"
                                    valueField="value"
                                    onChange={(o) => {
                                        const newVal = _.get(o, 'target.value');
                                        let ed = _.get(this.state, 'editData');
                                        ed.deleteAssocTypeDestId = newVal;
                                        this.setState({ editData: ed });
                                    }}
                                    {...inputPropsVirtualizedSelect}
                                />
                            </DetailFormCommon.InventoryDetailFormSection>
                        </div>
                    </div>
                );
                break;
            }

            case LK.LookupKeys.SidingTypes: {
                lookupObjName = `${Formatters.getStr(curEditData, 'name')} (${Formatters.getStr(curEditData, 'sidingTypeId')})`;
                modalTitle = this.appText('modals.delete-lookup-obj.siding-types.title');
                modalNote = this.appText('modals.delete-lookup-obj.siding-types.note');
                modalSuccessButtonText = this.appText('modals.delete-lookup-obj.siding-types.delete-object');
                modalBody = (
                    <div>
                        <div className="lookup-tables-modal-delete-lookup-obj-warning-containter">
                            {modalNote}
                        </div>

                        <div>
                            {this.appText('modals.delete-lookup-obj.siding-types.note-2')}
                        </div>

                        <div>
                            <DetailFormCommon.InventoryDetailFormSection title="">
                                <KendoWrappers.KendoDisabledInputHidden
                                    name="deleteLookupObj.targetSidingType"
                                    label={this.appText('modals.delete-lookup-obj.siding-types.pending-deletion')}
                                    value={lookupObjName}
                                    {...inputProps}
                                />
                                <KendoWrappers.KendoDropdownListInput
                                    name="deleteLookupObj.destObjId"
                                    clearable={false}
                                    label={this.appText('modals.delete-lookup-obj.siding-types.choose-default-type')}
                                    value={_.get(this.state, 'editData.deleteAssocTypeDestId')}
                                    data={_.get(formData, 'options.sidingTypes')}
                                    textField="text"
                                    valueField="value"
                                    onChange={(o) => {
                                        const newVal = _.get(o, 'target.value');
                                        let ed = _.get(this.state, 'editData');
                                        ed.deleteAssocTypeDestId = newVal;
                                        this.setState({ editData: ed });
                                    }}
                                    {...inputPropsVirtualizedSelect}
                                />
                            </DetailFormCommon.InventoryDetailFormSection>
                        </div>
                    </div>
                );
                break;
            }

            case LK.LookupKeys.SecuredTypes: {
                lookupObjName = `${Formatters.getStr(curEditData, 'name')} (${Formatters.getStr(curEditData, 'securedTypeId')})`;
                modalTitle = this.appText('modals.delete-lookup-obj.secured-types.title');
                modalNote = this.appText('modals.delete-lookup-obj.secured-types.note');
                modalSuccessButtonText = this.appText('modals.delete-lookup-obj.secured-types.delete-object');
                modalBody = (
                    <div>
                        <div className="lookup-tables-modal-delete-lookup-obj-warning-containter">
                            {modalNote}
                        </div>

                        <div>
                            {this.appText('modals.delete-lookup-obj.secured-types.note-2')}
                        </div>

                        <div>
                            <DetailFormCommon.InventoryDetailFormSection title="">
                                <KendoWrappers.KendoDisabledInputHidden
                                    name="deleteLookupObj.targetSecuredType"
                                    label={this.appText('modals.delete-lookup-obj.secured-types.pending-deletion')}
                                    value={lookupObjName}
                                    {...inputProps}
                                />
                                <KendoWrappers.KendoDropdownListInput
                                    name="deleteLookupObj.destObjId"
                                    clearable={false}
                                    label={this.appText('modals.delete-lookup-obj.secured-types.choose-default-type')}
                                    value={_.get(this.state, 'editData.deleteAssocTypeDestId')}
                                    data={_.get(formData, 'options.securedTypes')}
                                    textField="text"
                                    valueField="value"
                                    onChange={(o) => {
                                        const newVal = _.get(o, 'target.value');
                                        let ed = _.get(this.state, 'editData');
                                        ed.deleteAssocTypeDestId = newVal;
                                        this.setState({ editData: ed });
                                    }}
                                    {...inputPropsVirtualizedSelect}
                                />
                            </DetailFormCommon.InventoryDetailFormSection>
                        </div>
                    </div>
                );
                break;
            }

            case LK.LookupKeys.RecRoomTypes: {
                lookupObjName = `${Formatters.getStr(curEditData, 'name')} (${Formatters.getStr(curEditData, 'recRoomTypeId')})`;
                modalTitle = this.appText('modals.delete-lookup-obj.rec-room-types.title');
                modalNote = this.appText('modals.delete-lookup-obj.rec-room-types.note');
                modalSuccessButtonText = this.appText('modals.delete-lookup-obj.rec-room-types.delete-object');
                modalBody = (
                    <div>
                        <div className="lookup-tables-modal-delete-lookup-obj-warning-containter">
                            {modalNote}
                        </div>

                        <div>
                            {this.appText('modals.delete-lookup-obj.rec-room-types.note-2')}
                        </div>

                        <div>
                            <DetailFormCommon.InventoryDetailFormSection title="">
                                <KendoWrappers.KendoDisabledInputHidden
                                    name="deleteLookupObj.targetrecRoomType"
                                    label={this.appText('modals.delete-lookup-obj.rec-room-types.pending-deletion')}
                                    value={lookupObjName}
                                    {...inputProps}
                                />
                                <KendoWrappers.KendoDropdownListInput
                                    name="deleteLookupObj.destObjId"
                                    clearable={false}
                                    label={this.appText('modals.delete-lookup-obj.rec-room-types.choose-default-type')}
                                    value={_.get(this.state, 'editData.deleteAssocTypeDestId')}
                                    data={_.get(formData, 'options.recRoomTypes')}
                                    textField="text"
                                    valueField="value"
                                    onChange={(o) => {
                                        const newVal = _.get(o, 'target.value');
                                        let ed = _.get(this.state, 'editData');
                                        ed.deleteAssocTypeDestId = newVal;
                                        this.setState({ editData: ed });
                                    }}
                                    {...inputPropsVirtualizedSelect}
                                />
                            </DetailFormCommon.InventoryDetailFormSection>
                        </div>
                    </div>
                );
                break;
            }

            case LK.LookupKeys.StatusLevels: {
                lookupObjName = `${Formatters.getStr(curEditData, 'name')} (${Formatters.getStr(curEditData, 'statusId')})`;
                modalTitle = this.appText('modals.delete-lookup-obj.status-levels.title');
                modalNote = this.appText('modals.delete-lookup-obj.status-levels.note');
                modalSuccessButtonText = this.appText('modals.delete-lookup-obj.status-levels.delete-object');
                modalBody = (
                    <div>
                        <div className="lookup-tables-modal-delete-lookup-obj-warning-containter">
                            {modalNote}
                        </div>

                        <div>
                            {this.appText('modals.delete-lookup-obj.status-levels.note-2')}
                        </div>

                        <div>
                            <DetailFormCommon.InventoryDetailFormSection title="">
                                <KendoWrappers.KendoDisabledInputHidden
                                    name="deleteLookupObj.targetStatusId"
                                    label={this.appText('modals.delete-lookup-obj.status-levels.pending-deletion')}
                                    value={lookupObjName}
                                    {...inputProps}
                                />
                                <KendoWrappers.KendoDropdownListInput
                                    name="deleteLookupObj.destObjId"
                                    clearable={false}
                                    label={this.appText('modals.delete-lookup-obj.status-levels.choose-default-type')}
                                    value={_.get(this.state, 'editData.deleteAssocTypeDestId')}
                                    data={_.get(formData, 'options.statusLevels')}
                                    textField="text"
                                    valueField="value"
                                    onChange={(o) => {
                                        const newVal = _.get(o, 'target.value');
                                        let ed = _.get(this.state, 'editData');
                                        ed.deleteAssocTypeDestId = newVal;
                                        this.setState({ editData: ed });
                                    }}
                                    {...inputPropsVirtualizedSelect}
                                />
                            </DetailFormCommon.InventoryDetailFormSection>
                        </div>
                    </div>
                );
                break;
            }

            case LK.LookupKeys.Regions: {
                lookupObjName = `${Formatters.getStr(curEditData, 'name')} (${Formatters.getStr(curEditData, 'regionId')})`;
                modalTitle = this.appText('modals.delete-lookup-obj.regions.title');
                modalNote = this.appText('modals.delete-lookup-obj.regions.note');
                modalSuccessButtonText = this.appText('modals.delete-lookup-obj.regions.delete-object');
                modalBody = (
                    <div>
                        <div className="lookup-tables-modal-delete-lookup-obj-warning-containter">
                            {modalNote}
                        </div>

                        <div>
                            {this.appText('modals.delete-lookup-obj.regions.note-2')}
                        </div>

                        <div>
                            <DetailFormCommon.InventoryDetailFormSection title="">
                                <KendoWrappers.KendoDisabledInputHidden
                                    name="deleteLookupObj.targetRegionId"
                                    label={this.appText('modals.delete-lookup-obj.regions.pending-deletion')}
                                    value={lookupObjName}
                                    {...inputProps}
                                />
                                <KendoWrappers.KendoDropdownListInput
                                    name="deleteLookupObj.destObjId"
                                    clearable={false}
                                    label={this.appText('modals.delete-lookup-obj.regions.choose-default-type')}
                                    value={_.get(this.state, 'editData.deleteAssocTypeDestId')}
                                    data={_.get(formData, 'options.regions')}
                                    textField="text"
                                    valueField="value"
                                    onChange={(o) => {
                                        const newVal = _.get(o, 'target.value');
                                        let ed = _.get(this.state, 'editData');
                                        ed.deleteAssocTypeDestId = newVal;
                                        this.setState({ editData: ed });
                                    }}
                                    {...inputPropsVirtualizedSelect}
                                />
                            </DetailFormCommon.InventoryDetailFormSection>
                        </div>
                    </div>
                );
                break;
            }

            case LK.LookupKeys.ComponentLocations: {
                disabled = false;
                lookupObjName = `${Formatters.getStr(curEditData, 'name')} (${Formatters.getStr(curEditData, 'componentId')})`;
                modalTitle = this.appText('modals.delete-lookup-obj.component-locations.title');
                modalNote = this.appText('modals.delete-lookup-obj.component-locations.note');
                modalSuccessButtonText = this.appText('modals.delete-lookup-obj.component-locations.delete-object');
                modalBody = (
                    <div>
                        <div className="lookup-tables-modal-delete-lookup-obj-warning-containter">
                            {modalNote}
                        </div>

                        <div>
                            <DetailFormCommon.InventoryDetailFormSection title="">
                                <KendoWrappers.KendoDisabledInputHidden
                                    name="deleteLookupObj.targetComponentLocationsId"
                                    label={this.appText('modals.delete-lookup-obj.component-locations.pending-deletion')}
                                    value={lookupObjName}
                                    {...inputProps}
                                />
                            </DetailFormCommon.InventoryDetailFormSection>
                        </div>
                    </div>
                );
                break;
            }

            case LK.LookupKeys.InventoryTemplates: {
                disabled = false;
                lookupObjName = `${Formatters.getStr(curEditData, 'name')} (${Formatters.getStr(curEditData, 'templateId')})`;
                modalTitle = this.appText('modals.delete-lookup-obj.inventory-templates.title');
                modalNote = this.appText('modals.delete-lookup-obj.inventory-templates.note');
                modalSuccessButtonText = this.appText('modals.delete-lookup-obj.inventory-templates.delete-object');
                modalBody = (
                    <div>
                        <div className="lookup-tables-modal-delete-lookup-obj-warning-containter">
                            {modalNote}
                        </div>

                        <div>
                            <DetailFormCommon.InventoryDetailFormSection title="">
                                <KendoWrappers.KendoDisabledInputHidden
                                    name="deleteLookupObj.targetComponentLocationsId"
                                    label={this.appText('modals.delete-lookup-obj.inventory-templates.pending-deletion')}
                                    value={lookupObjName}
                                    {...inputProps}
                                />
                            </DetailFormCommon.InventoryDetailFormSection>
                        </div>
                    </div>
                );
                break;
            }

            case LK.LookupKeys.ComponentCategories: {
                lookupObjName = `${Formatters.getStr(curEditData, 'name')} (${Formatters.getStr(curEditData, 'categoryId')})`;
                modalTitle = this.appText('modals.delete-lookup-obj.component-categories.title');
                modalNote = this.appText('modals.delete-lookup-obj.component-categories.note');
                modalSuccessButtonText = this.appText('modals.delete-lookup-obj.component-categories.delete-object');
                modalBody = (
                    <div>
                        <div className="lookup-tables-modal-delete-lookup-obj-warning-containter">
                            {modalNote}
                        </div>

                        <div>
                            {this.appText('modals.delete-lookup-obj.component-categories.note-2')}
                        </div>

                        <div>
                            <DetailFormCommon.InventoryDetailFormSection title="">
                                <KendoWrappers.KendoDisabledInputHidden
                                    name="deleteLookupObj.targetComponentCategoryId"
                                    label={this.appText('modals.delete-lookup-obj.component-categories.pending-deletion')}
                                    value={lookupObjName}
                                    {...inputProps}
                                />
                                <KendoWrappers.KendoDropdownListInput
                                    name="deleteLookupObj.destObjId"
                                    clearable={false}
                                    label={this.appText('modals.delete-lookup-obj.component-categories.choose-default-type')}
                                    value={_.get(this.state, 'editData.deleteAssocTypeDestId')}
                                    data={_.get(formData, 'options.componentCategories')}
                                    textField="text"
                                    valueField="value"
                                    onChange={(o) => {
                                        const newVal = _.get(o, 'target.value');
                                        let ed = _.get(this.state, 'editData');
                                        ed.deleteAssocTypeDestId = newVal;
                                        this.setState({ editData: ed });
                                    }}
                                    {...inputPropsVirtualizedSelect}
                                />
                            </DetailFormCommon.InventoryDetailFormSection>
                        </div>
                    </div>
                );
                break;
            }

            case LK.LookupKeys.ReplacementCostTypes: {
                const docObjSeqIdField = 'replacementCostTypeId';
                const messagesPathName = 'replacement-cost-types';
                const docObjTypeName = 'ReplacementCostType';
                const formDataOptionsName = 'replacementCostTypes';

                lookupObjName = `${Formatters.getStr(curEditData, 'name')} (${Formatters.getStr(curEditData, docObjSeqIdField)})`;
                modalTitle = this.appText(`modals.delete-lookup-obj.${messagesPathName}.title`);
                modalNote = this.appText(`modals.delete-lookup-obj.${messagesPathName}.note`);
                modalSuccessButtonText = this.appText(`modals.delete-lookup-obj.${messagesPathName}.delete-object`);
                modalBody = (
                    <div>
                        <div className="lookup-tables-modal-delete-lookup-obj-warning-containter">
                            {modalNote}
                        </div>

                        <div>
                            {this.appText(`modals.delete-lookup-obj.${messagesPathName}.note-2`)}
                        </div>

                        <div>
                            <DetailFormCommon.InventoryDetailFormSection title="">
                                <KendoWrappers.KendoDisabledInputHidden
                                    name={`deleteLookupObj.target${docObjTypeName}`}
                                    label={this.appText(`modals.delete-lookup-obj.${messagesPathName}.pending-deletion`)}
                                    value={lookupObjName}
                                    {...inputProps}
                                />
                                <KendoWrappers.KendoDropdownListInput
                                    name="deleteLookupObj.destObjId"
                                    clearable={false}
                                    label={this.appText(`modals.delete-lookup-obj.${messagesPathName}.choose-default-type`)}
                                    value={_.get(this.state, 'editData.deleteAssocTypeDestId')}
                                    data={_.get(formData, `options.${formDataOptionsName}`)}
                                    textField="text"
                                    valueField="value"
                                    onChange={(o) => {
                                        const newVal = _.get(o, 'target.value');
                                        let ed = _.get(this.state, 'editData');
                                        ed.deleteAssocTypeDestId = newVal;
                                        this.setState({ editData: ed });
                                    }}
                                    {...inputPropsVirtualizedSelect}
                                />
                            </DetailFormCommon.InventoryDetailFormSection>
                        </div>
                    </div>
                );
                break;
            }

            case LK.LookupKeys.ServiceLevels: {
                const docObjSeqIdField = 'serviceLevelId';
                const messagesPathName = 'service-levels';
                const docObjTypeName = 'ServiceLevel';
                const formDataOptionsName = 'serviceLevels';

                lookupObjName = `${Formatters.getStr(curEditData, 'name')} (${Formatters.getStr(curEditData, docObjSeqIdField)})`;
                modalTitle = this.appText(`modals.delete-lookup-obj.${messagesPathName}.title`);
                modalNote = this.appText(`modals.delete-lookup-obj.${messagesPathName}.note`);
                modalSuccessButtonText = this.appText(`modals.delete-lookup-obj.${messagesPathName}.delete-object`);
                modalBody = (
                    <div>
                        <div className="lookup-tables-modal-delete-lookup-obj-warning-containter">
                            {modalNote}
                        </div>

                        <div>
                            {this.appText(`modals.delete-lookup-obj.${messagesPathName}.note-2`)}
                        </div>

                        <div>
                            <DetailFormCommon.InventoryDetailFormSection title="">
                                <KendoWrappers.KendoDisabledInputHidden
                                    name={`deleteLookupObj.target${docObjTypeName}`}
                                    label={this.appText(`modals.delete-lookup-obj.${messagesPathName}.pending-deletion`)}
                                    value={lookupObjName}
                                    {...inputProps}
                                />
                                <KendoWrappers.KendoDropdownListInput
                                    name="deleteLookupObj.destObjId"
                                    clearable={false}
                                    label={this.appText(`modals.delete-lookup-obj.${messagesPathName}.choose-default-type`)}
                                    value={_.get(this.state, 'editData.deleteAssocTypeDestId')}
                                    data={_.get(formData, `options.${formDataOptionsName}`)}
                                    textField="text"
                                    valueField="value"
                                    onChange={(o) => {
                                        const newVal = _.get(o, 'target.value');
                                        let ed = _.get(this.state, 'editData');
                                        ed.deleteAssocTypeDestId = newVal;
                                        this.setState({ editData: ed });
                                    }}
                                    {...inputPropsVirtualizedSelect}
                                />
                            </DetailFormCommon.InventoryDetailFormSection>
                        </div>
                    </div>
                );
                break;
            }

            case LK.LookupKeys.ReplacementCosts: {
                const docObjSeqIdField = 'replacementCostId';
                const messagesPathName = 'replacement-costs';
                const docObjTypeName = 'ReplacementCost';
                const formDataOptionsName = 'replacementCosts';

                lookupObjName = `${Formatters.getStr(curEditData, 'name')} (${Formatters.getStr(curEditData, docObjSeqIdField)})`;
                modalTitle = this.appText(`modals.delete-lookup-obj.${messagesPathName}.title`);
                modalNote = this.appText(`modals.delete-lookup-obj.${messagesPathName}.note`);
                modalSuccessButtonText = this.appText(`modals.delete-lookup-obj.${messagesPathName}.delete-object`);
                modalBody = (
                    <div>
                        <div className="lookup-tables-modal-delete-lookup-obj-warning-containter">
                            {modalNote}
                        </div>

                        <div>
                            {this.appText(`modals.delete-lookup-obj.${messagesPathName}.note-2`)}
                        </div>

                        <div>
                            <DetailFormCommon.InventoryDetailFormSection title="">
                                <KendoWrappers.KendoDisabledInputHidden
                                    name={`deleteLookupObj.target${docObjTypeName}`}
                                    label={this.appText(`modals.delete-lookup-obj.${messagesPathName}.pending-deletion`)}
                                    value={lookupObjName}
                                    {...inputProps}
                                />
                                <KendoWrappers.KendoDropdownListInput
                                    name="deleteLookupObj.destObjId"
                                    clearable={false}
                                    label={this.appText(`modals.delete-lookup-obj.${messagesPathName}.choose-default-type`)}
                                    value={_.get(this.state, 'editData.deleteAssocTypeDestId')}
                                    data={_.get(formData, `options.${formDataOptionsName}`)}
                                    textField="text"
                                    valueField="value"
                                    onChange={(o) => {
                                        const newVal = _.get(o, 'target.value');
                                        let ed = _.get(this.state, 'editData');
                                        ed.deleteAssocTypeDestId = newVal;
                                        this.setState({ editData: ed });
                                    }}
                                    {...inputPropsVirtualizedSelect}
                                />
                            </DetailFormCommon.InventoryDetailFormSection>
                        </div>
                    </div>
                );
                break;
            }

            case LK.LookupKeys.GroupComponentsStandardEvaluations: {
                const docObjSeqIdField = 'groupComponentObjectId';
                const messagesPathName = 'group-components-standard-evaluations';
                const docObjTypeName = 'GroupComponents';
                const formDataOptionsName = 'groupComponents';
                disabled = false;

                // lookupObjName = `${Formatters.getStr(curEditData, 'name')} (${Formatters.getStr(curEditData, docObjSeqIdField)})`;
                lookupObjName = `Component Id ${curEditData.componentId} - Group Id ${curEditData.groupId}`;
                modalTitle = this.appText(`modals.delete-lookup-obj.${messagesPathName}.title`);
                modalNote = this.appText(`modals.delete-lookup-obj.${messagesPathName}.note`);
                modalSuccessButtonText = this.appText(`modals.delete-lookup-obj.${messagesPathName}.delete-object`);
                modalBody = (
                    <div>
                        <div className="lookup-tables-modal-delete-lookup-obj-warning-containter">
                            {modalNote}
                        </div>

                        <div>
                            {this.appText(`modals.delete-lookup-obj.${messagesPathName}.note-2`)}
                        </div>

                        <div>
                            <DetailFormCommon.InventoryDetailFormSection title="">
                                <KendoWrappers.KendoDisabledInputHidden
                                    name={`deleteLookupObj.target${docObjTypeName}`}
                                    label={this.appText(`modals.delete-lookup-obj.${messagesPathName}.pending-deletion`)}
                                    value={lookupObjName}
                                    {...inputPropsFullWidth}
                                />
                            </DetailFormCommon.InventoryDetailFormSection>
                        </div>
                    </div>
                );
                break;
            }

            default:
                modalBody = (<div />);
                break;
        }

        return (
            <Modal enforceFocus={false} show={this.state.showModalDeleteLookupObj} onHide={() => this.handleCloseModalDeleteLookupObj()}>
                <Modal.Header closeButton={true}>
                    <Modal.Title>{modalTitle}?</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    {modalBody}
                </Modal.Body>
                <Modal.Footer>
                    <Button bsStyle="success" disabled={disabled} onClick={(e) => this.handleConfirmRemove()}>{modalSuccessButtonText}</Button>
                    <Button bsStyle="danger" onClick={() => this.handleCloseModalDeleteLookupObj()}>{this.appTextCommon('buttons.cancel')}</Button>
                </Modal.Footer>
            </Modal>
        );
    }

    public transformLookupData(lookupDataRaw: any, curEditData: any, curEditId: any) {
        let ldr = _.cloneDeep(lookupDataRaw);

        let agd = _.get(this.state, 'additionalGridData');

        if (agd && !_.isEmpty(agd)) { ldr.unshift(agd); }

        let lookupDataTransformed = _.map(ldr, (r) => {
            let isEditing = (curEditId === Formatters.getStr(r, '_id')) ? true : false;

            let result = _.cloneDeep(r);

            if (isEditing) {
                const editFieldOption = _.stubObject();
                _.set(editFieldOption, this.GridEditField, isEditing);
                result = _.merge({}, r, curEditData, editFieldOption);
            }

            return result;
        });

        return lookupDataTransformed;
    }

    public gridCommandCellDetailsButtonEnabled(dataItem: any): boolean {
        const { lookups, tableName, ...rest } = this.props;

        let result = false;

        switch (tableName) {
            case LK.LookupKeys.InventoryTemplates:
                result = true;
                break;

            case LK.LookupKeys.Components:
                // result = true;
                result = true;
                break;

            default:
                result = false;
                break;
        }

        return result;
    }

    public gridCommandCellDeleteButtonEnabled(dataItem: any): boolean {
        const { lookups, tableName, ...rest } = this.props;

        let result = true;

        switch (tableName) {
            case LK.LookupKeys.ReplacementCostTypes:
                if (Formatters.getInt(dataItem, 'replacementCostTypeId') <= 2) {
                    result = false;
                } else {
                    result = true;
                }

                break;

            case LK.LookupKeys.ServiceLevels:
                if (Formatters.getInt(dataItem, 'serviceLevelId') <= 15) {
                    result = false;
                } else {
                    result = true;
                }

                break;

            case LK.LookupKeys.StatusLevels:
                if (Formatters.getInt(dataItem, 'statusId') <= 15) {
                    result = false;
                } else {
                    result = true;
                }

                break;

            case LK.LookupKeys.UserResumes:
                result = false;

                break;
            default:
                result = true;
                break;
        }

        return result;
    }

    public hasAllValues = (dataItem: any, fields: Array<any>): boolean => _.every(fields, (o) => Formatters.getStr(dataItem, o) !== '');

    public render() {
        const { lookups, tableName, ...rest } = this.props;

        const requiredLookupKeys = _.uniq(_.compact([
            Formatters.getStr(this.props, 'match.params.tableName', tableName),
            LK.LookupKeys.ReplacementCostTypes,
        ]));
        const loadedLookupKeys = _.keys(lookups || {});
        const missingLookupKeys = _.filter(requiredLookupKeys, (k) => _.size(_.get(lookups, k, [])) <= 0);
        const hasAccessToken = Formatters.getStr(this.props, 'authsession.access_token') !== '';

        let lookupDataRaw = _.get(lookups, tableName, []);

        if (!_.size(lookupDataRaw)) {
            const pageTitle = this.appTextPageTitleAppendString(tableName, 'lookup-table', true);

            return (
                <AppComp.RenderPageWithLayout branding={this.props.branding} pageName={pageTitle} {...this.props}>
                    <div>
                        <Row>
                            <Col xs={12}>
                                <div>
                                    {this.renderHeaderButtonToolbar()}
                                </div>
                            </Col>
                        </Row>

                        <WhiteBoxPageSection title="Lookup Table Data Did Not Load">
                            <Row>
                                <Col xs={12}>
                                    <p>
                                        The lookup table editor could not load data for this table. This can happen if required
                                        lookup data is missing or the request is rejected.
                                    </p>
                                    <ul>
                                        <li>Requested table key: <strong>{String(tableName || '')}</strong></li>
                                        <li>Required lookup keys: <strong>{requiredLookupKeys.join(', ') || 'none'}</strong></li>
                                        <li>Missing lookup keys: <strong>{missingLookupKeys.join(', ') || 'none detected'}</strong></li>
                                        <li>Loaded lookup key count in state: <strong>{loadedLookupKeys.length}</strong></li>
                                        <li>Auth token present in session: <strong>{hasAccessToken ? 'yes' : 'no'}</strong></li>
                                    </ul>
                                    <p>
                                        Sensitive details are intentionally hidden. If this continues, share the table key and missing
                                        keys shown above with support.
                                    </p>
                                </Col>
                            </Row>
                        </WhiteBoxPageSection>
                    </div>
                </AppComp.RenderPageWithLayout>
            );
        }

        const curEditData = _.get(this.state, 'editData', {});
        const curEditId = Formatters.getStr(curEditData, '_id');

        let lookupDataTransformed = this.transformLookupData(lookupDataRaw, curEditData, curEditId);

        const appTextCol = (fieldName: string) => {
            return this.appText(`tables.${tableName}.${fieldName}`);
        };

        let renderGridToolbar = true;
        let gridProps: any = {};
        let gridCols = _.stubArray();

        const buildEditGridCommandCell = (vals: Array<any> = []) => {
            return EditGridCommandCell(
                (dataItem: any) => this.handleEnterEdit(dataItem),
                (e: any, dataItem: any) => this.handleShowModalDeleteLookupObj(e, dataItem),
                (dataItem: any) => this.handleSave(dataItem),
                (dataItem: any) => this.handleCreate(dataItem),
                (dataItem: any) => this.handleCancel(dataItem),
                (dataItem: any) => this.handleEnterDetails(dataItem),
                (dataItem: any) => this.gridCommandCellDetailsButtonEnabled(dataItem),
                (dataItem: any) => this.gridCommandCellDeleteButtonEnabled(dataItem),
                (dataItem: any) => this.hasAllValues(dataItem, vals),
                this.GridEditField,
                this.GridNewRecordField,
                (m?: any, p?: any, t?: any) => this.appText(m, p, t)
            );
        };

        const buildFilteredOptionsStringValue = (lookupData: any, key: any, srcValueField: any = 'name', srcTextField: any = 'name', removeId: string = '_') => {
            const lData = _.get(lookupData, key, []);

            const l = _.reject(lData, (o) => Formatters.getStr(o, '_id') === removeId);

            if (_.size(l) <= 0) { return []; }

            return _.orderBy(l.map((c) => _.merge({}, { value: Formatters.getStr(c, srcValueField), text: Formatters.getStr(c, srcTextField) })), 'text');
        };

        let formData = {
            options: {
                associationTypes: buildFilteredOptionsStringValue(lookups, LK.LookupKeys.AssociationTypes, '_id', 'name', curEditId),
                clientTypes: buildFilteredOptionsStringValue(lookups, LK.LookupKeys.ClientTypes, '_id', 'name', curEditId),
                roofTypes: buildFilteredOptionsStringValue(lookups, LK.LookupKeys.RoofTypes, '_id', 'name', curEditId),
                fencingTypes: buildFilteredOptionsStringValue(lookups, LK.LookupKeys.FencingTypes, '_id', 'name', curEditId),
                sidingTypes: buildFilteredOptionsStringValue(lookups, LK.LookupKeys.SidingTypes, '_id', 'name', curEditId),
                securedTypes: buildFilteredOptionsStringValue(lookups, LK.LookupKeys.SecuredTypes, '_id', 'name', curEditId),
                recRoomTypes: buildFilteredOptionsStringValue(lookups, LK.LookupKeys.RecRoomTypes, '_id', 'name', curEditId),
                componentCategories: buildFilteredOptionsStringValue(lookups, LK.LookupKeys.ComponentCategories, '_id', 'name', curEditId),
                statusLevels: buildFilteredOptionsStringValue(lookups, LK.LookupKeys.StatusLevels, '_id', 'name', curEditId),
                regions: buildFilteredOptionsStringValue(lookups, LK.LookupKeys.Regions, '_id', 'name', curEditId),
                replacementCostTypes: buildFilteredOptionsStringValue(lookups, LK.LookupKeys.ReplacementCostTypes, '_id', 'name', curEditId),
                serviceLevels: buildFilteredOptionsStringValue(lookups, LK.LookupKeys.ServiceLevels, '_id', 'name', curEditId),
                replacementCosts: buildFilteredOptionsStringValue(lookups, LK.LookupKeys.ReplacementCosts, '_id', 'explanation', curEditId),
            }
        };

        switch (tableName) {
            case LK.LookupKeys.TextContents: {
                gridCols.push(this.getGridCol('textContentKey', appTextCol('textContentKey'), { width: '100px', editable: false, sortable: true }));
                gridCols.push(this.getGridCol('name', appTextCol('name'), { width: '200px', editable: false }));
                gridCols.push(this.getGridCol('contents', appTextCol('contents'), { editable: false }));

                const editGridCommandCell = EditGridCommandCell(
                    (dataItem: any) => {
                        // this.handleEnterEdit(dataItem)
                        this.props.navigateTo(NavigationLinks.textContentEdit(Formatters.getStr(dataItem, '_id')));
                    },
                    (e: any, dataItem: any) => this.handleShowModalDeleteLookupObj(e, dataItem),
                    (dataItem: any) => this.handleSave(dataItem),
                    (dataItem: any) => this.handleCreate(dataItem),
                    (dataItem: any) => this.handleCancel(dataItem),
                    (dataItem: any) => this.handleEnterDetails(dataItem, LK.LookupKeys.TextContents),
                    (dataItem: any) => this.gridCommandCellDetailsButtonEnabled(dataItem),
                    (dataItem: any) => this.gridCommandCellDeleteButtonEnabled(dataItem),
                    (dataItem: any) => this.hasAllValues(dataItem, ['name']),
                    this.GridEditField,
                    this.GridNewRecordField,
                    (m?: any, p?: any, t?: any) => this.appText(m, p, t));

                gridCols.push(this.getGridCol('', '', { width: '201px', cell: editGridCommandCell }));

                gridProps.sortable = true;
                const defaultGridSort = [{ field: 'textContentKey', dir: 'asc' }];
                const curStateGridSortRaw = _.get(this.state, 'gridSort', defaultGridSort);
                const curStateGridSort = (_.isEmpty(curStateGridSortRaw)) ? defaultGridSort : curStateGridSortRaw;
                gridProps.sort = (_.size(curStateGridSort) > 0) ? curStateGridSort : defaultGridSort;
                gridProps.sortChange = (e: any) => {
                    const eventGridSortRaw = _.get(e, 'sort', defaultGridSort);
                    const eventGridSortNext = (_.isEmpty(eventGridSortRaw)) ? defaultGridSort : eventGridSortRaw;
                    this.setState({ 'gridSort': eventGridSortNext });
                };

                lookupDataTransformed = orderBy(lookupDataTransformed, curStateGridSort);
                break;
            }

            case LK.LookupKeys.AssociationTypes: {
                gridCols.push(this.getGridCol('associationTypeId', appTextCol('associationTypeId'), { width: '60px', editable: false, sortable: true }));
                gridCols.push(this.getGridCol('name', appTextCol('name'), { width: '300px', editable: true }));
                gridCols.push(this.getGridCol('description', appTextCol('description'), { editable: true }));

                const editGridCommandCell = buildEditGridCommandCell(['name']);

                gridCols.push(this.getGridCol('', '', { width: '201px', cell: editGridCommandCell }));

                gridProps.sortable = true;
                const defaultGridSort = [{ field: 'associationTypeId', dir: 'asc' }];
                const curStateGridSortRaw = _.get(this.state, 'gridSort', defaultGridSort);
                const curStateGridSort = (_.isEmpty(curStateGridSortRaw)) ? defaultGridSort : curStateGridSortRaw;
                gridProps.sort = (_.size(curStateGridSort) > 0) ? curStateGridSort : defaultGridSort;
                gridProps.sortChange = (e: any) => {
                    const eventGridSortRaw = _.get(e, 'sort', defaultGridSort);
                    const eventGridSortNext = (_.isEmpty(eventGridSortRaw)) ? defaultGridSort : eventGridSortRaw;
                    this.setState({ 'gridSort': eventGridSortNext });
                };

                lookupDataTransformed = orderBy(lookupDataTransformed, curStateGridSort);
                break;
            }

            case LK.LookupKeys.ClientTypes: {
                gridCols.push(this.getGridCol('clientTypeId', appTextCol('clientTypeId'), { width: '60px', editable: false, sortable: true }));
                gridCols.push(this.getGridCol('name', appTextCol('name'), { editable: true }));

                const editGridCommandCell = buildEditGridCommandCell(['name']);

                gridCols.push(this.getGridCol('', '', { width: '201px', cell: editGridCommandCell }));

                gridProps.sortable = true;
                const defaultGridSort = [{ field: 'clientTypeId', dir: 'asc' }];
                const curStateGridSortRaw = _.get(this.state, 'gridSort', defaultGridSort);
                const curStateGridSort = (_.isEmpty(curStateGridSortRaw)) ? defaultGridSort : curStateGridSortRaw;
                gridProps.sort = (_.size(curStateGridSort) > 0) ? curStateGridSort : defaultGridSort;
                gridProps.sortChange = (e: any) => {
                    const eventGridSortRaw = _.get(e, 'sort', defaultGridSort);
                    const eventGridSortNext = (_.isEmpty(eventGridSortRaw)) ? defaultGridSort : eventGridSortRaw;
                    this.setState({ 'gridSort': eventGridSortNext });
                };

                lookupDataTransformed = orderBy(lookupDataTransformed, curStateGridSort);
                break;
            }

            case LK.LookupKeys.StatusLevels: {
                gridCols.push(this.getGridCol('statusId', appTextCol('statusId'), { width: '60px', editable: false, sortable: true }));
                gridCols.push(this.getGridCol('name', appTextCol('name'), { editable: true }));

                const editGridCommandCell = buildEditGridCommandCell(['name']);

                gridCols.push(this.getGridCol('', '', { width: '201px', cell: editGridCommandCell }));

                gridProps.sortable = true;
                const defaultGridSort = [{ field: 'statusId', dir: 'asc' }];
                const curStateGridSortRaw = _.get(this.state, 'gridSort', defaultGridSort);
                const curStateGridSort = (_.isEmpty(curStateGridSortRaw)) ? defaultGridSort : curStateGridSortRaw;
                gridProps.sort = (_.size(curStateGridSort) > 0) ? curStateGridSort : defaultGridSort;
                gridProps.sortChange = (e: any) => {
                    const eventGridSortRaw = _.get(e, 'sort', defaultGridSort);
                    const eventGridSortNext = (_.isEmpty(eventGridSortRaw)) ? defaultGridSort : eventGridSortRaw;
                    this.setState({ 'gridSort': eventGridSortNext });
                };

                lookupDataTransformed = orderBy(lookupDataTransformed, curStateGridSort);
                break;
            }

            case LK.LookupKeys.FencingTypes: {
                gridCols.push(this.getGridCol('fencingTypeId', appTextCol('fencingTypeId'), { width: '60px', editable: false, sortable: true }));
                gridCols.push(this.getGridCol('name', appTextCol('name'), { editable: true }));
                gridCols.push(this.getGridCol('description', appTextCol('description'), { editable: true }));

                const editGridCommandCell = buildEditGridCommandCell(['name']);

                gridCols.push(this.getGridCol('', '', { width: '201px', cell: editGridCommandCell }));

                gridProps.sortable = true;
                const defaultGridSort = [{ field: 'fencingTypeId', dir: 'asc' }];
                const curStateGridSortRaw = _.get(this.state, 'gridSort', defaultGridSort);
                const curStateGridSort = (_.isEmpty(curStateGridSortRaw)) ? defaultGridSort : curStateGridSortRaw;
                gridProps.sort = (_.size(curStateGridSort) > 0) ? curStateGridSort : defaultGridSort;
                gridProps.sortChange = (e: any) => {
                    const eventGridSortRaw = _.get(e, 'sort', defaultGridSort);
                    const eventGridSortNext = (_.isEmpty(eventGridSortRaw)) ? defaultGridSort : eventGridSortRaw;
                    this.setState({ 'gridSort': eventGridSortNext });
                };

                lookupDataTransformed = orderBy(lookupDataTransformed, curStateGridSort);
                break;
            }

            case LK.LookupKeys.RoofTypes: {
                gridCols.push(this.getGridCol('roofTypeId', appTextCol('roofTypeId'), { width: '60px', editable: false, sortable: true }));
                gridCols.push(this.getGridCol('name', appTextCol('name'), { editable: true }));
                gridCols.push(this.getGridCol('description', appTextCol('description'), { editable: true }));

                const editGridCommandCell = buildEditGridCommandCell(['name']);

                gridCols.push(this.getGridCol('', '', { width: '201px', cell: editGridCommandCell }));

                gridProps.sortable = true;
                const defaultGridSort = [{ field: 'roofTypeId', dir: 'asc' }];
                const curStateGridSortRaw = _.get(this.state, 'gridSort', defaultGridSort);
                const curStateGridSort = (_.isEmpty(curStateGridSortRaw)) ? defaultGridSort : curStateGridSortRaw;
                gridProps.sort = (_.size(curStateGridSort) > 0) ? curStateGridSort : defaultGridSort;
                gridProps.sortChange = (e: any) => {
                    const eventGridSortRaw = _.get(e, 'sort', defaultGridSort);
                    const eventGridSortNext = (_.isEmpty(eventGridSortRaw)) ? defaultGridSort : eventGridSortRaw;
                    this.setState({ 'gridSort': eventGridSortNext });
                };

                lookupDataTransformed = orderBy(lookupDataTransformed, curStateGridSort);
                break;
            }

            case LK.LookupKeys.SidingTypes: {
                gridCols.push(this.getGridCol('sidingTypeId', appTextCol('sidingTypeId'), { width: '60px', editable: false, sortable: true }));
                gridCols.push(this.getGridCol('name', appTextCol('name'), { editable: true }));
                gridCols.push(this.getGridCol('description', appTextCol('description'), { editable: true }));

                const editGridCommandCell = buildEditGridCommandCell(['name']);

                gridCols.push(this.getGridCol('', '', { width: '201px', cell: editGridCommandCell }));

                gridProps.sortable = true;
                const defaultGridSort = [{ field: 'sidingTypeId', dir: 'asc' }];
                const curStateGridSortRaw = _.get(this.state, 'gridSort', defaultGridSort);
                const curStateGridSort = (_.isEmpty(curStateGridSortRaw)) ? defaultGridSort : curStateGridSortRaw;
                gridProps.sort = (_.size(curStateGridSort) > 0) ? curStateGridSort : defaultGridSort;
                gridProps.sortChange = (e: any) => {
                    const eventGridSortRaw = _.get(e, 'sort', defaultGridSort);
                    const eventGridSortNext = (_.isEmpty(eventGridSortRaw)) ? defaultGridSort : eventGridSortRaw;
                    this.setState({ 'gridSort': eventGridSortNext });
                };

                lookupDataTransformed = orderBy(lookupDataTransformed, curStateGridSort);
                break;
            }

            case LK.LookupKeys.SecuredTypes: {
                gridCols.push(this.getGridCol('securedTypeId', appTextCol('securedTypeId'), { width: '60px', editable: false, sortable: true }));
                gridCols.push(this.getGridCol('name', appTextCol('name'), { editable: true }));
                gridCols.push(this.getGridCol('description', appTextCol('description'), { editable: true }));

                const editGridCommandCell = buildEditGridCommandCell(['name']);

                gridCols.push(this.getGridCol('', '', { width: '201px', cell: editGridCommandCell }));

                gridProps.sortable = true;
                const defaultGridSort = [{ field: 'securedTypeId', dir: 'asc' }];
                const curStateGridSortRaw = _.get(this.state, 'gridSort', defaultGridSort);
                const curStateGridSort = (_.isEmpty(curStateGridSortRaw)) ? defaultGridSort : curStateGridSortRaw;
                gridProps.sort = (_.size(curStateGridSort) > 0) ? curStateGridSort : defaultGridSort;
                gridProps.sortChange = (e: any) => {
                    const eventGridSortRaw = _.get(e, 'sort', defaultGridSort);
                    const eventGridSortNext = (_.isEmpty(eventGridSortRaw)) ? defaultGridSort : eventGridSortRaw;
                    this.setState({ 'gridSort': eventGridSortNext });
                };

                lookupDataTransformed = orderBy(lookupDataTransformed, curStateGridSort);
                break;
            }

            case LK.LookupKeys.RecRoomTypes: {
                gridCols.push(this.getGridCol('recRoomTypeId', appTextCol('recRoomTypeId'), { width: '60px', editable: false, sortable: true }));
                gridCols.push(this.getGridCol('name', appTextCol('name'), { editable: true }));
                gridCols.push(this.getGridCol('description', appTextCol('description'), { editable: true }));

                const editGridCommandCell = buildEditGridCommandCell(['name']);

                gridCols.push(this.getGridCol('', '', { width: '201px', cell: editGridCommandCell }));

                gridProps.sortable = true;
                const defaultGridSort = [{ field: 'recRoomTypeId', dir: 'asc' }];
                const curStateGridSortRaw = _.get(this.state, 'gridSort', defaultGridSort);
                const curStateGridSort = (_.isEmpty(curStateGridSortRaw)) ? defaultGridSort : curStateGridSortRaw;
                gridProps.sort = (_.size(curStateGridSort) > 0) ? curStateGridSort : defaultGridSort;
                gridProps.sortChange = (e: any) => {
                    const eventGridSortRaw = _.get(e, 'sort', defaultGridSort);
                    const eventGridSortNext = (_.isEmpty(eventGridSortRaw)) ? defaultGridSort : eventGridSortRaw;
                    this.setState({ 'gridSort': eventGridSortNext });
                };

                lookupDataTransformed = orderBy(lookupDataTransformed, curStateGridSort);
                break;
            }

            case LK.LookupKeys.ComponentLocations: {
                gridCols.push(this.getGridCol('componentId', appTextCol('componentId'), { width: '120px', editable: false, sortable: true }));
                gridCols.push(this.getGridCol('name', appTextCol('name'), { width: '300px', editable: false, sortable: true }));
                gridCols.push(this.getGridCol('locations', appTextCol('locations'), { editable: true, sortable: true }));

                const editGridCommandCell = buildEditGridCommandCell(['componentId', 'location']);

                gridCols.push(this.getGridCol('', '', { width: '201px', cell: editGridCommandCell }));

                gridProps.sortable = true;
                const defaultGridSort = [{ field: 'componentId', dir: 'asc' }];
                const curStateGridSortRaw = _.get(this.state, 'gridSort', defaultGridSort);
                const curStateGridSort = (_.isEmpty(curStateGridSortRaw)) ? defaultGridSort : curStateGridSortRaw;
                gridProps.sort = (_.size(curStateGridSort) > 0) ? curStateGridSort : defaultGridSort;
                gridProps.sortChange = (e: any) => {
                    const eventGridSortRaw = _.get(e, 'sort', defaultGridSort);
                    const eventGridSortNext = (_.isEmpty(eventGridSortRaw)) ? defaultGridSort : eventGridSortRaw;
                    this.setState({ 'gridSort': eventGridSortNext });
                };

                lookupDataTransformed = orderBy(lookupDataTransformed, curStateGridSort);
                break;
            }

            case LK.LookupKeys.Regions: {
                gridCols.push(this.getGridCol('regionId', appTextCol('regionId'), { width: '60px', editable: false, sortable: true }));
                gridCols.push(this.getGridCol('name', appTextCol('name'), { width: '200px', editable: true }));
                gridCols.push(this.getGridCol('description', appTextCol('description'), { editable: true }));

                const editGridCommandCell = EditGridCommandCell(
                    (dataItem: any) => {
                        this.props.navigateTo(NavigationLinks.regionEdit(Formatters.getInt(dataItem, 'regionId')));
                    },
                    (e: any, dataItem: any) => this.handleShowModalDeleteLookupObj(e, dataItem),
                    (dataItem: any) => this.handleSave(dataItem),
                    (dataItem: any) => this.handleCreate(dataItem),
                    (dataItem: any) => this.handleCancel(dataItem),
                    (dataItem: any) => {
                        this.props.navigateTo(NavigationLinks.regionEdit(Formatters.getInt(dataItem, 'regionId')));
                    },
                    (dataItem: any) => this.gridCommandCellDetailsButtonEnabled(dataItem),
                    (dataItem: any) => this.gridCommandCellDeleteButtonEnabled(dataItem),
                    (dataItem: any) => this.hasAllValues(dataItem, ['name']),
                    this.GridEditField,
                    this.GridNewRecordField,
                    (m?: any, p?: any, t?: any) => this.appText(m, p, t));

                gridCols.push(this.getGridCol('', '', { width: '201px', cell: editGridCommandCell }));

                gridProps.sortable = true;
                const defaultGridSort = [{ field: 'regionId', dir: 'asc' }];
                const curStateGridSortRaw = _.get(this.state, 'gridSort', defaultGridSort);
                const curStateGridSort = (_.isEmpty(curStateGridSortRaw)) ? defaultGridSort : curStateGridSortRaw;
                gridProps.sort = (_.size(curStateGridSort) > 0) ? curStateGridSort : defaultGridSort;
                gridProps.sortChange = (e: any) => {
                    const eventGridSortRaw = _.get(e, 'sort', defaultGridSort);
                    const eventGridSortNext = (_.isEmpty(eventGridSortRaw)) ? defaultGridSort : eventGridSortRaw;
                    this.setState({ 'gridSort': eventGridSortNext });
                };

                lookupDataTransformed = orderBy(lookupDataTransformed, curStateGridSort);
                break;
            }

            case LK.LookupKeys.ComponentCategories: {
                gridCols.push(this.getGridCol('categoryId', appTextCol('categoryId'), { width: '60px', editable: false, sortable: true }));
                gridCols.push(this.getGridCol('name', appTextCol('name'), { width: '200px', editable: true }));
                gridCols.push(this.getGridCol('description', appTextCol('description'), { editable: true }));

                const editGridCommandCell = buildEditGridCommandCell(['name']);

                gridCols.push(this.getGridCol('', '', { width: '201px', cell: editGridCommandCell }));

                gridProps.sortable = true;
                const defaultGridSort = [{ field: 'categoryId', dir: 'asc' }];
                const curStateGridSortRaw = _.get(this.state, 'gridSort', defaultGridSort);
                const curStateGridSort = (_.isEmpty(curStateGridSortRaw)) ? defaultGridSort : curStateGridSortRaw;
                gridProps.sort = (_.size(curStateGridSort) > 0) ? curStateGridSort : defaultGridSort;
                gridProps.sortChange = (e: any) => {
                    const eventGridSortRaw = _.get(e, 'sort', defaultGridSort);
                    const eventGridSortNext = (_.isEmpty(eventGridSortRaw)) ? defaultGridSort : eventGridSortRaw;
                    this.setState({ 'gridSort': eventGridSortNext });
                };

                lookupDataTransformed = orderBy(lookupDataTransformed, curStateGridSort);
                break;
            }

            case LK.LookupKeys.Components: {
                gridCols.push(this.getGridCol('componentId', appTextCol('componentId'), { width: '60px', editable: false, sortable: true }));
                gridCols.push(this.getGridCol('componentCategoryId', appTextCol('componentCategoryId'), { width: '60px', editable: true, sortable: true }));
                gridCols.push(this.getGridCol('name', appTextCol('name'), { width: '350px', editable: true, sortable: true }));
                gridCols.push(this.getGridCol('summary', appTextCol('summary'), { editable: true }));
                // gridCols.push(this.getGridCol('inactive', appTextCol('inactive'), { width: '100px', editable: false }));
                gridCols.push(this.getGridCol('usefulLife', appTextCol('usefulLife'), { width: '100px', editable: true }));

                const editGridCommandCell = EditGridCommandCell(
                    // (dataItem: any) => this.handleEnterEdit(dataItem),
                    (dataItem: any) => {
                        this.props.navigateTo(NavigationLinks.componentEdit(Formatters.getStr(dataItem, '_id')));
                    },
                    (e: any, dataItem: any) => false, // this.handleShowModalDeleteLookupObj(e, dataItem),
                    (dataItem: any) => this.handleSave(dataItem),
                    (dataItem: any) => this.handleCreate(dataItem),
                    (dataItem: any) => this.handleCancel(dataItem),
                    (dataItem: any) => {
                        this.props.navigateTo(NavigationLinks.componentDetail(Formatters.getStr(dataItem, '_id')));
                    },
                    (dataItem: any) => this.gridCommandCellDetailsButtonEnabled(dataItem),
                    (dataItem: any) => false, // this.gridCommandCellDeleteButtonEnabled(dataItem),
                    (dataItem: any) => false, // this.hasAllValues(dataItem, ['name']),
                    this.GridEditField,
                    this.GridNewRecordField,
                    (m?: any, p?: any, t?: any) => this.appText(m, p, t));

                gridCols.push(this.getGridCol('', '', { width: '161px', cell: editGridCommandCell }));

                gridProps.sortable = true;
                const defaultGridSort = [
                    { field: 'componentCategoryId', dir: 'asc' },
                    { field: 'componentId', dir: 'asc' },
                ];
                // 20200128 was: const defaultGridSort = [{ field: 'componentId', dir: 'asc' }];
                const curStateGridSortRaw = _.get(this.state, 'gridSort', defaultGridSort);
                const curStateGridSort = (_.isEmpty(curStateGridSortRaw)) ? defaultGridSort : curStateGridSortRaw;
                gridProps.sort = (_.size(curStateGridSort) > 0) ? curStateGridSort : defaultGridSort;
                gridProps.sortChange = (e: any) => {
                    const eventGridSortRaw = _.get(e, 'sort', defaultGridSort);
                    const eventGridSortNext = (_.isEmpty(eventGridSortRaw)) ? defaultGridSort : eventGridSortRaw;
                    this.setState({ 'gridSort': eventGridSortNext });
                };

                lookupDataTransformed = orderBy(lookupDataTransformed, curStateGridSort);
                break;
            }

            case LK.LookupKeys.InventoryTemplates: {
                gridCols.push(this.getGridCol('templateId', appTextCol('templateId'), { width: '60px', editable: false, sortable: true }));
                gridCols.push(this.getGridCol('regionId', appTextCol('regionId'), { width: '80px', editable: true, sortable: true }));
                gridCols.push(this.getGridCol('groupId', appTextCol('groupId'), { width: '80px', editable: true, sortable: true }));
                gridCols.push(this.getGridCol('name', appTextCol('name'), { editable: true, sortable: true }));

                const editGridCommandCell = EditGridCommandCell(
                    (dataItem: any) => this.handleEnterEdit(dataItem),
                    (e: any, dataItem: any) => this.handleShowModalDeleteLookupObj(e, dataItem),
                    (dataItem: any) => this.handleSave(dataItem),
                    (dataItem: any) => this.handleCreate(dataItem),
                    (dataItem: any) => this.handleCancel(dataItem),
                    (dataItem: any) => this.handleEnterDetails(dataItem, LK.LookupKeys.InventoryTemplates),
                    (dataItem: any) => this.gridCommandCellDetailsButtonEnabled(dataItem),
                    (dataItem: any) => this.gridCommandCellDeleteButtonEnabled(dataItem),
                    (dataItem: any) => this.hasAllValues(dataItem, ['name']),
                    this.GridEditField,
                    this.GridNewRecordField,
                    (m?: any, p?: any, t?: any) => this.appText(m, p, t));

                gridCols.push(this.getGridCol('', '', { width: '201px', cell: editGridCommandCell }));

                gridProps.sortable = true;
                const defaultGridSort = [{ field: 'templateId', dir: 'asc' }];
                const curStateGridSortRaw = _.get(this.state, 'gridSort', defaultGridSort);
                const curStateGridSort = (_.isEmpty(curStateGridSortRaw)) ? defaultGridSort : curStateGridSortRaw;
                gridProps.sort = (_.size(curStateGridSort) > 0) ? curStateGridSort : defaultGridSort;
                gridProps.sortChange = (e: any) => {
                    const eventGridSortRaw = _.get(e, 'sort', defaultGridSort);
                    const eventGridSortNext = (_.isEmpty(eventGridSortRaw)) ? defaultGridSort : eventGridSortRaw;
                    this.setState({ 'gridSort': eventGridSortNext });
                };

                lookupDataTransformed = orderBy(lookupDataTransformed, curStateGridSort);
                break;
            }

            case LK.LookupKeys.InventoryTemplatesGroupComponents: {
                const invTemplates = _.get(lookupDataTransformed, '[0]', []);
                const groupComponents = _.get(lookupDataTransformed, '[1]', []);

                lookupDataTransformed = _.map(invTemplates, (invTemplate) => {
                    const invTemplateObjId = Formatters.getStr(invTemplate, '_id');
                    const invTemplateGroups = _.filter(groupComponents, (o) => {
                        const groupTemplateItems = _.get(o, 'inventoryTemplateItems', []);
                        const groupTemplateItemsForTemplate = _.filter(groupTemplateItems, ['templateObjectId', invTemplateObjId]);

                        return (_.size(groupTemplateItemsForTemplate)) ? true : false;
                    });

                    const invTemplateGroupItems = _(invTemplateGroups)
                        .map(o => {
                            const itemCompObjId = Formatters.getStr(o, 'componentObjectId');
                            const items = _.get(o, 'inventoryTemplateItems', []);

                            if (itemCompObjId && _.size(items)) {
                                items.forEach(e => {
                                    _.set(e, 'componentObjectId', itemCompObjId);
                                    _.set(e, 'templateName', Formatters.getStr(invTemplate, 'name'));
                                    _.set(e, 'templateId', Formatters.getStr(invTemplate, 'templateId'));
                                    _.set(e, 'componentId', Formatters.getInt(o, 'componentId'));
                                });
                            }

                            return items;
                        })
                        .compact()
                        .flatten()
                        .value();

                    return invTemplateGroupItems;
                });

                lookupDataTransformed = _.flatten(lookupDataTransformed);
                lookupDataTransformed = _.orderBy(lookupDataTransformed, ['templateId', 'componentId'], ['asc', 'asc']);
                lookupDataTransformed = groupBy(lookupDataTransformed, [{ field: 'templateName' }]);

                gridCols.push(this.getGridCol('componentId', appTextCol('componentId'), {}));
                gridCols.push(this.getGridCol('location', appTextCol('location'), {}));
                gridCols.push(this.getGridCol('quantity', appTextCol('quantity'), {}));
                gridCols.push(this.getGridCol('usefulLife', appTextCol('usefulLife'), {}));

                gridProps.groupable = true;
                gridProps.group = [{ field: 'templateName' }];

                break;
            }

            case LK.LookupKeys.ReplacementCostTypes: {
                gridCols.push(this.getGridCol('replacementCostTypeId', appTextCol('replacementCostTypeId'), { width: '60px', editable: false, sortable: true }));
                gridCols.push(this.getGridCol('name', appTextCol('name'), { width: '200px', editable: true, sortable: true }));
                gridCols.push(this.getGridCol('description', appTextCol('description'), { editable: true }));

                const editGridCommandCell = buildEditGridCommandCell(['name', 'description']);

                gridCols.push(this.getGridCol('', '', { width: '201px', cell: editGridCommandCell }));
                gridProps.sortable = true;

                const defaultGridSort = [{ field: 'replacementCostTypeId', dir: 'asc' }];
                const curStateGridSortRaw = _.get(this.state, 'gridSort', defaultGridSort);
                const curStateGridSort = (_.isEmpty(curStateGridSortRaw)) ? defaultGridSort : curStateGridSortRaw;
                gridProps.sort = (_.size(curStateGridSort) > 0) ? curStateGridSort : defaultGridSort;
                gridProps.sortChange = (e: any) => {
                    const eventGridSortRaw = _.get(e, 'sort', defaultGridSort);
                    const eventGridSortNext = (_.isEmpty(eventGridSortRaw)) ? defaultGridSort : eventGridSortRaw;
                    this.setState({ 'gridSort': eventGridSortNext });
                };

                lookupDataTransformed = orderBy(lookupDataTransformed, curStateGridSort);

                break;
            }

            case LK.LookupKeys.UserResumes: {
                lookupDataTransformed = _.map(lookupDataTransformed, (o) => {
                    _.set(o, 'username', Formatters.getStr(o, 'user.username'));
                    _.set(o, 'userFullName', Formatters.composeClientContactLetter(_.get(o, 'user', {}), true, false));

                    const findStatePermit = (userObj: any, stateCode: string) => _.find(_.get(userObj, 'permits', []), ['location.stateCode', stateCode]);
                    const statePermitNV = findStatePermit(o, 'NV');

                    _.set(o, 'nevadaPermit', Formatters.getStr(statePermitNV, 'permitNumber'));

                    return o;
                });

                gridCols.push(this.getGridCol('username', appTextCol('username'), { width: '100px', editable: false, sortable: true }));
                gridCols.push(this.getGridCol('userFullName', appTextCol('userFullName'), { width: '150px', editable: false, sortable: true }));
                gridCols.push(this.getGridCol('nevadaPermit', appTextCol('nevadaPermit'), { width: '150px', editable: true }));

                gridCols.push(this.getGridCol('resume', appTextCol('resume'), { cell: this.BuildGridHtmlEditorCell }));

                const editGridCommandCell = buildEditGridCommandCell(['resume']);
                gridCols.push(this.getGridCol('', '', { width: '201px', cell: editGridCommandCell }));

                gridProps.sortable = true;
                const defaultGridSort = [{ field: 'username', dir: 'asc' }];
                const curStateGridSortRaw = _.get(this.state, 'gridSort', defaultGridSort);
                const curStateGridSort = (_.isEmpty(curStateGridSortRaw)) ? defaultGridSort : curStateGridSortRaw;
                gridProps.sort = (_.size(curStateGridSort) > 0) ? curStateGridSort : defaultGridSort;
                gridProps.sortChange = (e: any) => {
                    const eventGridSortRaw = _.get(e, 'sort', defaultGridSort);
                    const eventGridSortNext = (_.isEmpty(eventGridSortRaw)) ? defaultGridSort : eventGridSortRaw;
                    this.setState({ 'gridSort': eventGridSortNext });
                };

                // Re-apply any pending edits/changes
                lookupDataTransformed = this.transformLookupData(lookupDataTransformed, curEditData, curEditId);

                lookupDataTransformed = orderBy(lookupDataTransformed, curStateGridSort);
                renderGridToolbar = false;

                break;
            }

            case LK.LookupKeys.ServiceLevels: {
                gridCols.push(this.getGridCol('serviceLevelId', appTextCol('serviceLevelId'), { width: '40px', editable: false }));
                gridCols.push(this.getGridCol('name', appTextCol('name'), { width: '120px', editable: true }));
                gridCols.push(this.getGridCol('report', appTextCol('report'), { width: '80px', editable: true }));
                gridCols.push(this.getGridCol('timeframe', appTextCol('timeframe'), { width: '80px', editable: true }));

                gridCols.push(this.getGridCol('description', appTextCol('description'), { editable: true }));
                gridCols.push(this.getGridCol('serviceLevelList', appTextCol('serviceLevelList'), { editable: true, cell: this.BuildGridHtmlEditorCell }));
                gridCols.push(this.getGridCol('introDescription', appTextCol('introDescription'), { editable: true }));

                const editGridCommandCell = buildEditGridCommandCell(['name']);
                gridCols.push(this.getGridCol('', '', { width: '151px', cell: editGridCommandCell }));

                gridProps.sortable = true;
                const defaultGridSort = [{ field: 'serviceLevelId', dir: 'asc' }];
                const curStateGridSortRaw = _.get(this.state, 'gridSort', defaultGridSort);
                const curStateGridSort = (_.isEmpty(curStateGridSortRaw)) ? defaultGridSort : curStateGridSortRaw;
                gridProps.sort = (_.size(curStateGridSort) > 0) ? curStateGridSort : defaultGridSort;
                gridProps.sortChange = (e: any) => {
                    const eventGridSortRaw = _.get(e, 'sort', defaultGridSort);
                    const eventGridSortNext = (_.isEmpty(eventGridSortRaw)) ? defaultGridSort : eventGridSortRaw;
                    this.setState({ 'gridSort': eventGridSortNext });
                };

                lookupDataTransformed = orderBy(lookupDataTransformed, curStateGridSort);

                break;
            }

            case LK.LookupKeys.ReplacementCosts: {
                const dd = buildFilteredOptionsStringValue(lookups, LK.LookupKeys.ReplacementCostTypes, 'replacementCostTypeId', 'name', '');

                const cellDropDownCostType = (props) => (
                    <CellDropDown
                        recordEditingField={this.GridEditField}
                        handleDropDownChange={(a) => { this.handleCellDropdownChange(a); }}
                        dropdownData={dd}
                        {...props} />);

                gridCols.push(this.getGridCol('replacementCostId', appTextCol('replacementCostId'), { width: '70px', editable: false }));
                gridCols.push(this.getGridCol('componentId', appTextCol('componentId'), { width: '110px', editable: true, editor: 'numeric' }));
                gridCols.push(this.getGridCol('groupId', appTextCol('groupId'), { width: '80px', editable: true, editor: 'numeric' }));

                gridCols.push(this.getGridCol('replacementCostType.name', appTextCol('replacementCostType'), { width: '150px', editable: true, cell: cellDropDownCostType }));

                gridCols.push(this.getGridCol('cost', appTextCol('cost'), { width: '100px', editable: true, format: '{0:c2}', editor: 'numeric' }));
                gridCols.push(this.getGridCol('explanation', appTextCol('explanation'), { editable: true }));
                gridCols.push(this.getGridCol('notes', appTextCol('notes'), { editable: true }));

                const editGridCommandCell = buildEditGridCommandCell(['componentId', 'replacementCostType.name']);
                gridCols.push(this.getGridCol('', '', { width: '151px', cell: editGridCommandCell }));

                gridProps.sortable = true;
                const defaultGridSort = [
                    { field: this.GridNewRecordField, dir: 'desc' },
                    { field: this.GridEditField, dir: 'desc' },
                    { field: 'componentId', dir: 'asc' },
                    { field: 'replacementCostType.name', dir: 'asc' }
                ];
                const curStateGridSortRaw = _.get(this.state, 'gridSort', defaultGridSort);
                const curStateGridSort = (_.isEmpty(curStateGridSortRaw)) ? defaultGridSort : curStateGridSortRaw;
                gridProps.sort = (_.size(curStateGridSort) > 0) ? curStateGridSort : defaultGridSort;
                gridProps.sortChange = (e: any) => {
                    const eventGridSortRaw = _.get(e, 'sort', defaultGridSort);
                    const eventGridSortNext = (_.isEmpty(eventGridSortRaw)) ? defaultGridSort : eventGridSortRaw;
                    this.setState({ 'gridSort': eventGridSortNext });
                };

                lookupDataTransformed = orderBy(lookupDataTransformed, curStateGridSort);

                gridProps.total = _.size(lookupDataTransformed);
                gridProps.style = { height: '600px' };

                break;
            }

            case LK.LookupKeys.GroupComponentsStandardEvaluations: {
                lookupDataTransformed = _.map(lookupDataTransformed, (r) => {
                    const standardEvaluations = _.get(r, 'standardComponentEvaluations', []);

                    if (_.size(standardEvaluations)) {
                        _.set(r, 'conditionLevel', Formatters.getInt(standardEvaluations, '[0].conditionLevel'));
                        _.set(r, 'evaluation', Formatters.getStr(standardEvaluations, '[0].evaluation'));
                    }

                    return r;
                });

                lookupDataTransformed = _.orderBy(lookupDataTransformed, ['componentId', 'conditionLevel'], ['asc', 'asc']);

                const hasNew = _.some(lookupDataTransformed, (o) => Formatters.getBool(o, this.GridNewRecordField));

                let editable = hasNew;

                gridCols.push(this.getGridCol('componentId', appTextCol('componentId'), { width: '120px', editable: editable, editor: 'numeric' }));
                gridCols.push(this.getGridCol('conditionLevel', appTextCol('conditionLevel'), { width: '120px', editable: editable, editor: 'numeric' }));
                gridCols.push(this.getGridCol('groupId', appTextCol('groupId'), { width: '100px', editable: editable, editor: 'numeric' }));
                gridCols.push(this.getGridCol('evaluation', appTextCol('evaluation'), { editable: true }));

                const editGridCommandCell = buildEditGridCommandCell(['componentId', 'evaluation']);
                gridCols.push(this.getGridCol('', '', { width: '151px', cell: editGridCommandCell }));

                gridProps.sortable = true;
                const defaultGridSort = [
                    { field: this.GridNewRecordField, dir: 'desc' },
                    { field: this.GridEditField, dir: 'desc' },
                    { field: 'componentId', dir: 'asc' },
                    { field: 'groupId', dir: 'asc' },
                    { field: 'conditionLevel', dir: 'asc' }
                ];
                const curStateGridSortRaw = _.get(this.state, 'gridSort', defaultGridSort);
                const curStateGridSort = (_.isEmpty(curStateGridSortRaw)) ? defaultGridSort : curStateGridSortRaw;
                gridProps.sort = (_.size(curStateGridSort) > 0) ? curStateGridSort : defaultGridSort;
                gridProps.sortChange = (e: any) => {
                    const eventGridSortRaw = _.get(e, 'sort', defaultGridSort);
                    const eventGridSortNext = (_.isEmpty(eventGridSortRaw)) ? defaultGridSort : eventGridSortRaw;
                    this.setState({ 'gridSort': eventGridSortNext });
                };

                lookupDataTransformed = orderBy(lookupDataTransformed, curStateGridSort);

                break;
            }

            default:
                break;
        }

        const gridDataSource = process(lookupDataTransformed, {});

        const addNewButtonDisabled = this.handleAddNewButtonDisabled(tableName);
        const addNewButtonHandler = (tName: string, e: any) => this.handleAddNewButton(tName, e);
        const gridToolbar = (renderGridToolbar) ? (
            <GridToolbar>
                <Button
                    bsSize="xs"
                    bsStyle="success"
                    // onClick={(e) => this.handleAddNewButton(e)}
                    onClick={(e: any) => addNewButtonHandler(tableName, e)}
                    disabled={addNewButtonDisabled}
                >
                    <i className="glyphicon glyphicon-plus" />  {this.appTextCommon('buttons.new')}
                </Button>
            </GridToolbar>

        ) : (<div />);

        const grid = (
            <Grid
                data={gridDataSource}
                resizable={true}
                editField={this.GridEditField}

                itemChange={(e) => { this.handleGridItemChange(e); }}

                {...gridProps}
            >
                {gridToolbar}
                {_.map(gridCols, (c, i) => this.renderGridCol(c, i))}
            </Grid>
        );

        const pageTitle = this.appTextPageTitleAppendString(tableName, 'lookup-table', true);
        const page = (
            <div>
                <Row>
                    <Col xs={12} xl={9}>
                        <div>
                            {this.renderHeaderButtonToolbar()}
                            {this.renderModalDeleteLookupObj(formData)}
                        </div>
                    </Col>
                </Row>
                <WhiteBoxPageSection title={pageTitle}>
                    {/* Header toolbar */}
                    <Row>
                        <Col xs={12} className="lookup-tables-edit">
                            {grid}
                        </Col>
                    </Row>
                </WhiteBoxPageSection>
            </div>
        );

        return (
            <AppComp.RenderPageWithLayout branding={this.props.branding} pageName={pageTitle} {...this.props}>
                {page}
            </AppComp.RenderPageWithLayout>
        );
    }
}

const getApiUrlLookups = (props) => ApiEndpoints.getLookupsByKeyUrl([
    props.match.params.tableName,
    LK.LookupKeys.ReplacementCostTypes,
]);

const mapPropsToConfigs = (props) => {
    return ReduxQueries.Queries.getLookupsByKeyRequest(
        getApiUrlLookups(props),
        ReduxQueries.PropsAccessToken(props)
    );
};

const mapStateToProps = (state, props) => {
    const tableName = Formatters.getStr(props, 'match.params.tableName');

    return ReduxQueries.MapQueryStateToPropsByUrl(getApiUrlLookups(props), state, props, {
        tableName: tableName,
    });
};

const mapDispatchToProps = {
    updateLookupObj: MutateActions.updateLookupObjMutateAsync,
    newLookupObj: MutateActions.newLookupObjMutateAsync,
    newInventoryComponent: MutateActions.newInventoryComponentObjMutateAsync,
    deleteLookupObj: MutateActions.deleteLookupObjMutateAsync,
    navigateTo: (path) => push(path),
};

const EditLookupTableContainer = compose<any>(
    connect(mapStateToProps, mapDispatchToProps),
    connectRequest(mapPropsToConfigs)
)(EditLookupTable);

export default withRouter(EditLookupTableContainer);



// WEBPACK FOOTER //
// ./src/lookup-tables/edit.tsx